import {
  Box,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  Stack,
  Typography,
} from "@mui/material";

import { renderErrorOrLoading } from "../commons/renderErrorOrLoading";
import type { ResultValue } from "../data/types";
import { useRow } from "../data/useRow";
import { MatchTime } from "./MatchTime";
import { MatchupImage } from "./MatchupImage";
import { Odds } from "./Odds";

interface MatchupViewProps {
  date?: Date;
  stage?: string;
  title?: string;
  home?: string;
  away?: string;
  score?: string;
  kickoff?: string;
  city?: string;
  venue?: string;
  description?: string;
  image?: string;
  oddsHome?: number;
  oddsDraw?: number;
  oddsAway?: number;
  result?: ResultValue;
  multiplier?: number;
  index?: number;
  onOpen: () => void;
}

type MatchupScheduleProps = Pick<MatchupViewProps, "date" | "kickoff" | "stage">;
type MatchupBodyProps = Pick<
  MatchupViewProps,
  | "away"
  | "city"
  | "date"
  | "description"
  | "home"
  | "image"
  | "kickoff"
  | "score"
  | "stage"
  | "title"
  | "venue"
>;
type MatchupActionsProps = Pick<
  MatchupViewProps,
  "multiplier" | "oddsAway" | "oddsDraw" | "oddsHome" | "result"
> & {
  currentPick?: ResultValue;
};

export function MatchupView({
  title,
  description,
  home,
  away,
  stage,
  score,
  onOpen,
  image,
  multiplier,
  oddsHome,
  oddsAway,
  oddsDraw,
  result,
  date,
  kickoff,
  city,
  venue,
  index,
}: MatchupViewProps): JSX.Element {
  const { myRow, error, loading } = useRow();
  const errorOrLoading = renderErrorOrLoading({ loading, error });
  const currentPick = pickForMatchup(myRow, index);

  if (errorOrLoading) return errorOrLoading;

  return (
    <Box
      id={`matchup-${index}`}
      tabIndex={-1}
      style={{
        // Breathing room above the card when "Go to latest" scrolls/focuses it.
        scrollMarginTop: "96px",
        width: "100%",
      }}
    >
      <Card
        onClick={onOpen}
        sx={{
          maxWidth: 380,
          minWidth: 0,
          width: "100%",
        }}
      >
        <CardActionArea>
          <MatchupBody
            away={away}
            city={city}
            date={date}
            description={description}
            home={home}
            image={image}
            kickoff={kickoff}
            score={score}
            stage={stage}
            title={title}
            venue={venue}
          />
        </CardActionArea>
        <MatchupActions
          currentPick={currentPick}
          multiplier={multiplier}
          oddsAway={oddsAway}
          oddsDraw={oddsDraw}
          oddsHome={oddsHome}
          result={result}
        />
      </Card>
    </Box>
  );
}

function MatchupSchedule({
  date,
  kickoff,
  stage,
}: MatchupScheduleProps): JSX.Element | null {
  if (!date && !kickoff && !stage) return null;

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      gap={2}
      sx={{ mb: 1 }}
    >
      <MatchTime date={date} kickoff={kickoff} />
      {stage && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 600,
            letterSpacing: 0.5,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
          }}
        >
          {stage}
        </Typography>
      )}
    </Stack>
  );
}

function MatchupBody({
  away,
  city,
  date,
  description,
  home,
  image,
  kickoff,
  score,
  stage,
  title,
  venue,
}: MatchupBodyProps): JSX.Element {
  const primaryTitle = matchupTitle({ away, home, title });
  const secondaryTitle = title && title !== primaryTitle ? title : undefined;
  const venueLine = [venue, city].filter(Boolean).join(", ");

  return (
    <>
      <MatchupImage home={home} away={away} title={primaryTitle} image={image} />
      <CardContent>
        <MatchupSchedule date={date} kickoff={kickoff} stage={stage} />
        <Stack direction="row" justifyContent="space-between" gap={2}>
          <Typography gutterBottom variant="h5" component="h2">
            {primaryTitle}
          </Typography>
          {score && (
            <Typography
              gutterBottom
              variant="h5"
              component="p"
              sx={{ fontWeight: 700, whiteSpace: "nowrap" }}
            >
              {score}
            </Typography>
          )}
        </Stack>
        {secondaryTitle && (
          <Typography variant="body2" color="text.secondary">
            {secondaryTitle}
          </Typography>
        )}
        {venueLine && (
          <Typography variant="body2" color="text.secondary">
            {venueLine}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        )}
      </CardContent>
    </>
  );
}

function MatchupActions({
  currentPick,
  multiplier,
  oddsAway,
  oddsDraw,
  oddsHome,
  result,
}: MatchupActionsProps): JSX.Element {
  return (
    <CardActions disableSpacing>
      <Odds
        oddsHome={oddsHome}
        oddsAway={oddsAway}
        oddsDraw={oddsDraw}
        result={result}
        myRow={currentPick}
      />

      {Boolean(multiplier) && (
        <Box sx={{ ml: "auto", pr: 1 }} data-testid="matchup-multiplier">
          x {multiplier}
        </Box>
      )}
    </CardActions>
  );
}

function matchupTitle({
  away,
  home,
  title,
}: Pick<MatchupViewProps, "away" | "home" | "title">): string {
  const teams = [home, away].filter(Boolean).join(" - ");

  return teams || title || "";
}

function pickForMatchup(
  row: string | undefined,
  index: number | undefined,
): ResultValue | undefined {
  return row && index !== undefined ? (row[index] as ResultValue) : undefined;
}
